import { Injectable, inject, signal } from '@angular/core';
import { environment } from '../../../environments/environment';
import { AuthService } from '../../core/auth/auth.service';
import { ActionFormRequest, ChatConversation, ChatMessage } from '../../models/chat.model';

const SESSION_KEY  = 'chat_session_id';
const TITLES_KEY   = 'chat_custom_titles';

@Injectable({ providedIn: 'root' })
export class ChatService {
  private auth = inject(AuthService);

  readonly messages      = signal<ChatMessage[]>([]);
  readonly streaming     = signal(false);
  readonly sessionId     = signal<string | null>(localStorage.getItem(SESSION_KEY));
  readonly conversations = signal<ChatConversation[]>([]);
  readonly projectContext = signal<string>('');

  setProjectContext(name: string) { this.projectContext.set(name); }
  clearProjectContext()           { this.projectContext.set(''); }

  get userProfilePicture(): string | null {
    return this.auth.currentUser()?.profile_picture ?? null;
  }

  get userName(): string {
    return this.auth.currentUser()?.name ?? '';
  }

  private _customTitles: Record<string, string> = JSON.parse(
    localStorage.getItem(TITLES_KEY) ?? '{}'
  );

  // ── Send ──────────────────────────────────────────────────────────────────

  async sendMessage(
    text: string,
    attachments: { type: string; mime_type: string; data: string }[] = [],
    userFacingMessage?: string,
  ) {
    this.messages.update(msgs => [
      ...msgs,
      { id: crypto.randomUUID(), role: 'user', content: userFacingMessage ?? text },
    ]);
    this.streaming.set(true);

    const assistantId = crypto.randomUUID();
    this.messages.update(msgs => [
      ...msgs,
      { id: assistantId, role: 'assistant', content: '', partial: true },
    ]);

    try {
      const response = await fetch(`${environment.apiBaseUrl}/api/v1/chat/stream`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          'Authorization': `Bearer ${this.auth.token}`,
        },
        body: JSON.stringify({
          session_id: this.sessionId(),
          message: text,
          attachments,
        }),
      });

      if (!response.ok || !response.body) throw new Error(`HTTP ${response.status}`);

      const reader  = response.body.getReader();
      const decoder = new TextDecoder();
      let buffer = '';

      while (true) {
        const { done, value } = await reader.read();
        if (done) break;
        buffer += decoder.decode(value, { stream: true });
        const lines = buffer.split('\n');
        buffer = lines.pop() ?? '';
        for (const line of lines) {
          if (!line.startsWith('data: ')) continue;
          try { this._handleEvent(JSON.parse(line.slice(6)), assistantId, text); }
          catch { /* malformed line */ }
        }
      }
    } catch {
      this.messages.update(msgs => msgs.map(m =>
        m.id === assistantId
          ? { ...m, content: 'An error occurred. Please try again.', partial: false }
          : m
      ));
    } finally {
      this.streaming.set(false);
    }
  }

  // ── Form submission ───────────────────────────────────────────────────────

  async getForm(toolName: string): Promise<ActionFormRequest | null> {
    try {
      const res = await fetch(`${environment.apiBaseUrl}/api/v1/chat/form/${toolName}`, {
        headers: { 'Authorization': `Bearer ${this.auth.token}` },
      });
      if (!res.ok) return null;
      return await res.json() as ActionFormRequest;
    } catch { return null; }
  }

  async submitForm(tool: string, title: string, data: Record<string, string>) {
    const payload = `__FORM__:${JSON.stringify({ tool, data })}`;
    await this.sendMessage(payload, [], `Submitting: ${title}…`);
  }

  markFormSubmitted(msgId: string) {
    this.messages.update(msgs =>
      msgs.map(m => m.id === msgId ? { ...m, formSubmitted: true } : m)
    );
  }

  // ── Event handler ─────────────────────────────────────────────────────────

  private _handleEvent(
    event: Record<string, unknown>,
    assistantId: string,
    firstUserText: string,
  ) {
    switch (event['event_type']) {
      case 'session_created': {
        const sid = event['session_id'] as string;
        this.sessionId.set(sid);
        localStorage.setItem(SESSION_KEY, sid);
        if (!this._customTitles[sid]) {
          this._setTitle(sid, firstUserText.slice(0, 60));
        }
        this.loadConversations();
        break;
      }
      case 'partial_text':
        this.messages.update(msgs => msgs.map(m =>
          m.id === assistantId
            ? { ...m, content: m.content + (event['content'] as string) }
            : m
        ));
        break;
      case 'final_text':
        this.messages.update(msgs => msgs.map(m =>
          m.id === assistantId
            ? { ...m, content: event['content'] as string, partial: false }
            : m
        ));
        break;
      case 'form_request': {
        const prefilled = { ...(event['prefilled'] as Record<string, string>) };
        // Auto-inject current project context when the agent didn't extract one
        if (!prefilled['project_name'] && this.projectContext()) {
          prefilled['project_name'] = this.projectContext();
        }
        this.messages.update(msgs => msgs.map(m =>
          m.id === assistantId
            ? {
                ...m,
                partial: false,
                formRequest: {
                  tool:      event['tool']   as string,
                  title:     event['title']  as string,
                  icon:      event['icon']   as string,
                  fields:    event['fields'] as ActionFormRequest['fields'],
                  prefilled,
                },
              }
            : m
        ));
        break;
      }
      case 'suggestion_chips':
        this.messages.update(msgs => msgs.map(m =>
          m.id === assistantId
            ? { ...m, suggestionChips: event['chips'] as string[] }
            : m
        ));
        break;
      case 'tool_call':
        this.messages.update(msgs => msgs.map(m =>
          m.id === assistantId ? { ...m, toolCalling: event['tool'] as string } : m
        ));
        break;
      case 'done':
        this.messages.update(msgs => msgs.map(m =>
          m.id === assistantId ? { ...m, partial: false, toolCalling: undefined } : m
        ));
        break;
      case 'error':
        this.messages.update(msgs => msgs.map(m =>
          m.id === assistantId
            ? { ...m, content: (event['content'] as string) || 'Error occurred.', partial: false }
            : m
        ));
        break;
    }
  }

  // ── Conversations ─────────────────────────────────────────────────────────

  async loadConversations() {
    try {
      const res  = await fetch(`${environment.apiBaseUrl}/api/v1/chat/conversations`, {
        headers: { 'Authorization': `Bearer ${this.auth.token}` },
      });
      const data = await res.json();
      const list: ChatConversation[] = data.conversations ?? [];
      this.conversations.set(
        list.map(c => ({ ...c, title: this._customTitles[c.session_id] ?? c.title }))
      );
    } catch { /* ignore */ }
  }

  async loadConversation(sessionId: string) {
    try {
      const res  = await fetch(`${environment.apiBaseUrl}/api/v1/chat/conversations/${sessionId}`, {
        headers: { 'Authorization': `Bearer ${this.auth.token}` },
      });
      const data = await res.json();
      if (data.messages) {
        const msgs: ChatMessage[] = (data.messages as any[]).map(m => ({
          id:              m.id,
          role:            m.role,
          content:         m.content,
          timestamp:       m.timestamp,
          formRequest:     m.form_request      ?? undefined,
          formSubmitted:   m.form_submitted    ?? undefined,
          suggestionChips: m.suggestion_chips  ?? undefined,
        }));
        this.messages.set(msgs);
        this.sessionId.set(sessionId);
        localStorage.setItem(SESSION_KEY, sessionId);
      }
    } catch { /* ignore */ }
  }

  async deleteConversation(sessionId: string) {
    try {
      await fetch(`${environment.apiBaseUrl}/api/v1/chat/conversations/${sessionId}`, {
        method: 'DELETE',
        headers: { 'Authorization': `Bearer ${this.auth.token}` },
      });
      delete this._customTitles[sessionId];
      this._persistTitles();
      this.conversations.update(list => list.filter(c => c.session_id !== sessionId));
      if (this.sessionId() === sessionId) this.newConversation();
    } catch { /* ignore */ }
  }

  renameConversation(sessionId: string, title: string) {
    const trimmed = title.trim().slice(0, 80);
    if (!trimmed) return;
    this._setTitle(sessionId, trimmed);
    this.conversations.update(list =>
      list.map(c => c.session_id === sessionId ? { ...c, title: trimmed } : c)
    );
  }

  newConversation() {
    this.sessionId.set(null);
    localStorage.removeItem(SESSION_KEY);
    this.messages.set([]);
  }

  private _setTitle(sessionId: string, title: string) {
    this._customTitles[sessionId] = title;
    this._persistTitles();
  }

  private _persistTitles() {
    localStorage.setItem(TITLES_KEY, JSON.stringify(this._customTitles));
  }
}
